
import { Component, Vue } from 'vue-property-decorator'
import { Form as ElForm } from 'element-ui'
import { MediaService } from '@/services/index'
import { isValidScore, isValidEmail } from '@/common/utils'
import { convertCodeToText, convertTextToCode } from '@/common/area_data'
import { AppModule } from '@/store/modules/app'
import { IMG_HOST } from '@/config/env'
import { debounce, pick } from 'lodash'
import * as echarts from 'echarts'
import UploadImg from '@/components/upload_img/index.vue'
import LoadMoreList from '@/components/load_more_list/index.vue'

@Component({
    name: 'MediaEdit',
    components: {
        UploadImg,
        LoadMoreList
    }
})
export default class extends Vue {
    private validateScore = (rule: any, value: number, callback: Function) => {
        if (value > 1000) {
            callback(new Error('请输入正确的评分，0-1000的数字'))
        } else if (!isValidScore(value)) {
            callback(new Error('请输入正确的评分，最多1位小数'))
        } else {
            callback()
        }
    }

    private validateEmail = (rule: any, value: string, callback: Function) => {
        if (!value) callback()
        if (!isValidEmail(value)) {
            callback(new Error('请输入正确的Email'))
        } else {
            callback()
        }
    }

    private mediaRules = {
        name: [
            { required: true, message: '请输入媒体名称', trigger: 'blur' },
            { min: 2, max: 20, message: '请输入正确的名称，2-20个字符', trigger: 'blur' }
        ],
        score: [{ validator: this.validateScore, trigger: 'blur' }],
        intro: [{ min: 2, max: 1000, message: '请输入正确的媒体介绍，2-1000个字符', trigger: 'blur' }],
        type: [{ required: true, message: '请选择媒体属性', trigger: 'blur' }]
    }

    private focus!: boolean
    private suggestMap!: Map<any, any>
    private isUpdateLoading = false
    private inputPopver = false
    private isDeleteLoading = false
    private fetchLoading = false
    private loading = true
    private mediaName = ''
    private mediaForm: any = {}
    private mediaType = 0
    private mediaId = ''
    private popverWidth = ''
    private mediaRegion: any = []
    private orgItem = { name: '', id: '' }
    private suggestList: any[] = []
    private mediaTypes: any[] = []
    private mediaMpTypes: any[] = []
    private activeTab = 'stats'
    private fansChart: any = null

    private pageIndex = 1
    private pageSize = 50
    private pageTotal = 0
    private fetchSuggest = debounce(async () => {
        this._reset()
        this.fetchLoading = true
        await this.getSuggest()
        this.fetchLoading = false
    }, 300)

    get provinceAndCityData() {
        return AppModule.provinceAndCity
    }
    get hasMore() {
        return this.suggestList.length < this.pageTotal
    }
    get isSMedia() {
        return +this.mediaId > 10000000
    }

    get emptyPlatforms() {
        return (this.mediaForm.platforms || []).filter(p => !p.fans_count || p.fans_count === 0)
    }

    private async mounted() {
        this.$title('Loading | 媒体详情 - 庖丁')
        const parsed: any = new URLSearchParams(location.search)
        this.activeTab = parsed.get('tab') || 'stats'
        await this.initData()
        this.loading = false
        this.calcPopverWidth()
        this.listenBodyClick()
        window.addEventListener('resize', this.handleWindowResize)
    }

    private beforeDestroy() {
        window.removeEventListener('resize', this.handleWindowResize)
        if (this.fansChart) {
            this.fansChart.dispose()
        }
    }

    private handleWindowResize = () => {
        if (this.fansChart) {
            this.fansChart.resize()
        }
    }

    private renderFansChart() {
        const chartEl = this.$refs.fansChart as HTMLElement
        if (!chartEl) return

        // 过滤有数据的平台
        const platforms = (this.mediaForm.platforms || []).filter(p => p.fans_count > 0)
        // 按粉丝数降序排列，取前10个
        platforms.sort((a, b) => b.fans_count - a.fans_count)
        const topPlatforms = platforms.slice(0, 10)

        const names = topPlatforms.map(p => p.name)
        const counts = topPlatforms.map(p => p.fans_count)

        if (!this.fansChart) {
            this.fansChart = echarts.init(chartEl)
        }

        const option = {
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'shadow'
                },
                formatter: (params: any) => {
                    const data = params[0]
                    return `${data.name}<br/>粉丝数: ${this.formatNumber(data.value)}`
                }
            },
            grid: {
                left: '3%',
                right: '4%',
                bottom: '3%',
                top: '8%',
                containLabel: true
            },
            xAxis: {
                type: 'category',
                data: names,
                axisLabel: {
                    rotate: 30,
                    fontSize: 12,
                    color: '#606266'
                },
                axisLine: {
                    lineStyle: {
                        color: '#eef1f6'
                    }
                }
            },
            yAxis: {
                type: 'value',
                name: '粉丝数',
                nameTextStyle: {
                    color: '#909399',
                    fontSize: 12
                },
                axisLabel: {
                    formatter: (value: number) => {
                        if (value >= 10000) {
                            return (value / 10000).toFixed(1) + '万'
                        }
                        return value.toString()
                    },
                    color: '#606266'
                },
                axisLine: {
                    show: false
                },
                splitLine: {
                    lineStyle: {
                        color: '#f0f4fa',
                        type: 'dashed'
                    }
                }
            },
            series: [
                {
                    data: counts,
                    type: 'bar',
                    barWidth: '50%',
                    itemStyle: {
                        borderRadius: [4, 4, 0, 0],
                        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                            { offset: 0, color: '#667eea' },
                            { offset: 1, color: '#764ba2' }
                        ])
                    },
                    emphasis: {
                        itemStyle: {
                            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                                { offset: 0, color: '#7c8eea' },
                                { offset: 1, color: '#865ba2' }
                            ])
                        }
                    }
                }
            ]
        }

        this.fansChart.setOption(option)
    }

    private onSwitchTab(tab: any) {
        const url = new URL(window.location.href)
        url.searchParams.set('tab', tab.name)
        window.history.pushState({}, '', url.toString())
    }

    private formatNumber(num: number): string {
        if (num >= 10000) {
            return (num / 10000).toFixed(1) + '万'
        }
        return num.toString()
    }

    private async initData() {
        this.mediaId = this.$route.params.id
        this.mediaType = this.isSMedia ? 1 : 0
        await Promise.all([
            this.fetchRegion(),
            this.fetchMediaTypes(),
            this.fetchMediaMpTypes()
        ])
        await this.fetchMediaDetail(parseInt(this.mediaId))
        this.$forceUpdate()
    }

    private async fetchMediaTypes() {
        const result = await MediaService.getMediaTypes()
        if (result.status === 200) {
            this.mediaTypes = result.data
        }
    }

    private async fetchMediaMpTypes(params?: any) {
        const result = await MediaService.getMediaMpTypes(params)
        if (result.status === 200) {
            this.mediaMpTypes = result.data
        }
    }

    private async fetchMediaDetail(mediaId: number) {
        const params = {
            id: mediaId,
            media_type: this.mediaType === 0 ? 'media' : 'self_media'
        }
        const result = await MediaService.getDetail(params)
        if (result.status === 200) {
            this.mediaForm = this.filterAlias(result.data)
            this.mediaForm.media_type = result.data.media_type || 'media'
            this.mediaType = this.mediaForm.media_type === 'media' ? 0 : 1
            this.mediaForm.type = this.filterTypes(this.mediaForm)
            this.mediaRegion = convertTextToCode((this.mediaForm.region || '').split(',').filter(Boolean))
            this.mediaName = result.data.name
            this.orgItem.name = (result.data.organization && result.data.organization.name) || ''
            this.$title(`${this.mediaName} | 媒体详情 - 庖丁`)
            // 延迟渲染图表
            setTimeout(() => {
                this.renderFansChart()
            }, 300)
        } else {
            this.$message({ type: 'error', message: result.message })
        }
    }

    private filterAlias(media: any) {
        media.aliasArr = media.alias || []
        media.alias = ''
        return media
    }

    private filterTypes(media: any) {
        const keys: any[] = []
        this.mediaTypes.map((item: any) => {
            keys.push(item.id)
        })
        if (keys.indexOf(media.type) === -1) {
            media.type = null
        }
        return media.type
    }

    private async fetchRegion() {
        await AppModule.getRegion()
    }

    private uploadCallback(url: string) {
        const $mediaForm = this.$refs.mediaForm as ElForm
        this.mediaForm.logo = IMG_HOST + url
        $mediaForm.validateField('logo')
    }

    private onSelectRegion(value: any) {
        this.mediaRegion = value
        this.mediaForm.region = convertCodeToText(value)
    }

    private async onUpdateMedia() {
        (this.$refs.mediaForm as ElForm).validate(async (valid: boolean) => {
            if (valid) {
                this.isUpdateLoading = true
                const attrs = [
                    'id', 'name', 'type', 'mp_type_ids', 'region', 'score',
                    'url', 'address', 'email', 'phone', 'logo', 'intro', 'aliasArr'
                ]
                const needParams = pick(this.mediaForm, attrs)
                const checkRegion = (region: string) => region.includes(',') ? region : `${region},`
                const newMediaType = +this.mediaType === 0 ? 'media' : 'self_media'
                const params = {
                    ...needParams,
                    logo: needParams.logo ? needParams.logo.replace(IMG_HOST, '') : '',
                    alias: needParams.aliasArr || [],
                    region: checkRegion(needParams.region || ''),
                    org_id: +this.orgItem.id || undefined,
                    media_type: this.mediaForm.media_type,
                    new_media_type: newMediaType,
                    is_closed: this.mediaForm.is_closed
                }
                const isDiffMediaType = this.mediaForm.media_type !== newMediaType
                if (isDiffMediaType) {
                    this.$confirm('您更改了当前媒体的媒体类型，当前媒体会被删除，是否继续操作', '提示', {
                        confirmButtonText: '确定',
                        cancelButtonText: '取消',
                        type: 'warning'
                    }).then(async () => {
                        const result = await MediaService.update(params)
                        if (result.status === 200) {
                            this.$message({ type: 'success', message: '修改成功' })
                        } else {
                            this.$message({ type: 'error', message: result.message })
                        }
                        this.isUpdateLoading = false
                    }).catch(() => {
                        this.isUpdateLoading = false
                    })
                } else {
                    const result = await MediaService.update(params)
                    if (result.status === 200) {
                        this.$message({ type: 'success', message: '修改成功' })
                    } else {
                        this.$message({ type: 'error', message: result.message })
                    }
                    this.isUpdateLoading = false
                }
            }
        })
    }

    private onCancel() {
        this.$router.go(-1)
    }

    private onRemove() {
        this.$confirm('确认删除此媒体?', '提示', {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
        })
            .then(async () => {
                this.isDeleteLoading = true
                const result = await MediaService.remove({ id: parseInt(this.mediaId) })
                if (result.status === 200) {
                    this.$message({ type: 'success', message: '删除成功' })
                    this.$router.go(-1)
                } else {
                    this.$message({ type: 'error', message: '删除媒体失败' })
                }
                this.isDeleteLoading = false
            })
            .catch(() => {
                this.$message({ type: 'info', message: '已取消操作' })
            })
    }

    private onGoNewsList() {
        const mediaId = this.mediaForm.id
        const name = this.mediaForm.name
        window.open('/news/list?mediaId=' + mediaId + '&name=' + name)
    }

    private onGoReporters() {
        const mediaId = this.mediaForm.id
        const name = this.mediaForm.name
        window.open('/reporter/list?mediaId=' + mediaId + '&name=' + name)
    }

    private onMediaAliasEnd() {
        (this.$refs.mediaForm as ElForm).validateField('alias')
        if (!this.mediaForm.alias) {
            return
        }
        this.mediaForm.aliasArr.push(this.mediaForm.alias)
        this.mediaForm.alias = ''
    }

    private onCloseAlias(index: number) {
        this.mediaForm.aliasArr.splice(index, 1)
    }

    private calcPopverWidth() {
        if (this.$refs.orgInputRef) {
            this.popverWidth = (this.$refs.orgInputRef as any).$el.clientWidth
        }
    }

    private onGetLoadMore() {
        this.pageIndex += 1
        this.getSuggest({ more: true })
    }

    private async onInputFocus() {
        this.focus = true
        if (!this.suggestList.length) {
            this.fetchLoading = true
            await this.getSuggest()
            this.fetchLoading = false
        }
        this.inputPopver = true
    }

    private onInputBlur() {
        this.focus = false
    }

    private async getSuggest({ more } = { more: false }) {
        const self = this
        const params = {
            query: this.orgItem.name,
            page_index: this.pageIndex,
            page_size: this.pageSize
        }
        const result = await MediaService.getOrgList(params)
        if (result.status === 200) {
            this.pageTotal = result.data.page_total
            if (more) {
                this.suggestList = this.suggestList.concat(result.data.org_list)
                concat(result.data.org_list)
            } else {
                this.suggestList = result.data.org_list
                this.suggestMap = toNewMap(result.data.org_list)
            }
        }
        function concat(list: any[]) {
            list.forEach(n => {
                self.suggestMap.set(n.id, n)
            })
        }
        function toNewMap(list: any[]) {
            let map = new Map()
            list.forEach(n => {
                map.set(n.id, n)
            })
            return map
        }
    }

    private listenBodyClick() {
        document.documentElement.addEventListener('click', this.closeCustomPopover)
        this.$once('hook:beforeDestroy', () => {
            document.removeEventListener('click', this.closeCustomPopover)
        })
    }

    private closeCustomPopover(event: any) {
        if (!this.inputPopver || this.focus) return
        const { target } = event
        const popver = (this.$refs.customPopover as any).popperElm
        if (!popver) return
        const isContain = popver.contains(target)
        if (!isContain) {
            this.inputPopver = false
        }
    }

    private onSelectSuggest(e: any) {
        const { id } = e.target.dataset
        if (!id) return
        const seletItem = this.suggestMap.get(+id)
        this.orgItem = seletItem
        this.inputPopver = false
    }

    private _reset() {
        this.pageIndex = 1
        this.pageTotal = 0
        this.suggestList = []
        this.suggestMap = new Map()
    }
}
